import React, { useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { useIntl } from 'react-intl';
import { findNodeByLocale } from '../../../../helpers/translations';
import MobileNavigation from './mobile-navigation';
import { NavigationContainer } from '../../types';
import DesktopNavigation from './desktop-navigation';
import { Paths } from '../../../../types/gatsby';

/** @knipignore */
export const navigationItemFragment = graphql`
  fragment NavigationItem on Node {
    ... on ContentfulSiteCategory {
      id
      title
      path
    }
    ... on ContentfulProductContent {
      id
      title: productName
      path
    }
    ... on ContentfulNavItem {
      id
      title
      path: url
    }
    ... on ContentfulPageContainer {
      id
      title
      path
    }
    ... on ContentfulCareerPage {
      id
      title
      path
    }
    ... on ContentfulPageSystem {
      id
      title
      path
    }
  }
`;

/** @knipignore */
export const navigationFragment = graphql`
  fragment Navigation on ContentfulNavigation {
    id
    title
    type
    node_locale
    items: contentfulchildren {
      ... on ContentfulNavItem {
        id
        title
        icon
        path: url
        label {
          id
          displayName
          type
        }
        audienceType
        linkedEntry: category {
          ...NavigationItem
        }
        collapsibleContent: contentfulchildren {
          ... on ContentfulNavItem {
            id
            title
            label {
              id
              displayName
              type
            }
            audienceType
            linkedEntry: category {
              ...NavigationItem
            }
            subItems: contentfulchildren {
              ...NavigationItem
              ... on ContentfulNavItem {
                id
                title
                path: url
                label {
                  id
                  displayName
                  type
                }
                audienceType
                linkedEntry: category {
                  ...NavigationItem
                }
              }
            }
          }
        }
      }
    }
  }
`;

/** @knipignore */
export const navigationContainerFragment = graphql`
  fragment NavigationContainer on ContentfulNavigationContainer {
    node_locale
    type
    navigation {
      ...Navigation
      ... on ContentfulExperiment {
        title
        experimentId
        control {
          ...Navigation
        }
        variant {
          ...Navigation
        }
      }
    }
  }
`;

const navigationsQuery = graphql`
  query {
    allCategoriesNavigations: allContentfulNavigationContainer(
      filter: {type: {in: ["All"]}}
    ) { nodes { ... NavigationContainer } }

    featuredNavigations: allContentfulNavigationContainer(
      filter: {type: {in: ["Featured"]}}
    ) { nodes { ... NavigationContainer } }

    supplementaryNavigations: allContentfulNavigationContainer(
      filter: {type: {in: ["Supplementary"]}}
    ) { nodes { ... NavigationContainer } }
  }
`;

type NavigationsQueryResult = {
  localizedPaths: Paths;
  allCategoriesNavigations: { nodes: NavigationContainer[] };
  featuredNavigations: { nodes: NavigationContainer[] };
  supplementaryNavigations: { nodes: NavigationContainer[] };
};

type MainNavigationProps = {
  localizedPaths?: Paths;
  className?: string;
};

const MainNavigation = ({
  localizedPaths,
  className,
}: MainNavigationProps) => {
  const intl = useIntl();

  const {
    allCategoriesNavigations,
    featuredNavigations,
    supplementaryNavigations,
  } = useStaticQuery<NavigationsQueryResult>(navigationsQuery);

  const allCategoriesNavigationForLocale: NavigationContainer['navigation'] = useMemo(() => findNodeByLocale(
    allCategoriesNavigations.nodes,
    intl.locale,
  )?.navigation || ({} as NavigationContainer['navigation']), [allCategoriesNavigations, intl.locale]);

  const featuredNavigationForLocale: NavigationContainer['navigation'] = useMemo(() => findNodeByLocale(
    featuredNavigations.nodes,
    intl.locale,
  )?.navigation || ({} as NavigationContainer['navigation']), [featuredNavigations, intl.locale]);

  const supplementaryNavigationForLocale: NavigationContainer['navigation'] = useMemo(() => findNodeByLocale(
    supplementaryNavigations.nodes,
    intl.locale,
  )?.navigation || ({} as NavigationContainer['navigation']), [supplementaryNavigations, intl.locale]);

  return (
    <nav className={className} aria-label={allCategoriesNavigationForLocale.title}>
      <MobileNavigation
        localizedPaths={localizedPaths}
        allCategoriesNavigation={allCategoriesNavigationForLocale}
        featuredNavigation={featuredNavigationForLocale}
        supplementaryNavigation={supplementaryNavigationForLocale}
      />
      <DesktopNavigation
        allCategoriesNavigation={allCategoriesNavigationForLocale}
        featuredNavigation={featuredNavigationForLocale}
        supplementaryNavigation={supplementaryNavigationForLocale}
      />
    </nav>
  );
};

export default MainNavigation;
